import { Avatar, Box, IconButton, Tooltip, Typography } from '@mui/material'
import { getGridStringOperators } from '@mui/x-data-grid-pro'
import ApproverBadge from 'components/Approvals/grid/cells/ApproverBadge'
import { getDensityStyles } from 'components/DataGrid/operators/getDensityStyles'
import ProgressCell from 'components/MTB/grid/cells/ProgressCell'
import { Link } from 'react-router-dom'
import EntityAvatar from 'theme/components/Avatar/EntityAvatar'
import { formatDayjsDate } from 'utils/formatDayjsDateTime'

const getStatusColor = progress => {
  if (progress === 1) {
    return 'rgb(6, 255, 167, 0.6)'
  } else if (progress === 2) {
    return 'rgba(220, 20, 60, 0.9)'
  } else if (progress === 3) {
    return 'rgb(249, 167, 49)' // Resolved or pending
  } else {
    return 'rgb(211, 211, 211)' // Pending or no action taken
  }
}

// Tooltip text for each status
const getStatusText = progress => {
  if (progress === 1) {
    return `Approved`
  } else if (progress === 2) {
    return `Rejected`
  } else if (progress === 3) {
    return `Resolved`
  } else {
    return `has not taken any action yet`
  }
}

export const renderApprovalsColumns = (deals, users, theme, density = 'standard') => {
  const densityStyles = getDensityStyles(density)
  const containsOperator = getGridStringOperators().find(op => op.value === 'contains')

  const customContainsApprover = {
    label: 'contains approver',
    value: 'contains',
    getApplyFilterFn: filterItem => {
      if (!filterItem.value) {
        return null
      }
      return params => {
        // Access approver names and check for the active user's name
        const approvers = params?.value?.approvers || []
        const approverNames = approvers.map(approver => approver.name)
        return approverNames.includes(filterItem.value)
      }
    },
  }

  // Custom operator that wraps the 'contains' logic to invert it
  const customNotContainsOperator = {
    ...containsOperator,
    label: 'does not contain',
    value: 'notContains',
    getApplyFilterFn: (filterItem: any, column: any) => {
      if (filterItem.value === undefined || filterItem.value === null) return null

      const containsFn = containsOperator?.getApplyFilterFn?.(
        { ...filterItem, value: filterItem.value.toString() },
        column
      )

      return (cellValue: any) =>
        !containsFn?.(cellValue ? cellValue.toString() : '', null as any, null as any, null as any)
    },
  }

  return [
    {
      field: 'name',
      headerName: 'Deal Name',
      width: 100,
      flex: 1.0,
      headerAlign: 'left',
      valueGetter: params => {
        return params
      },
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      renderCell: params => {
        const isApproved = params && params.row && params.row.isApproved
        const isRequested = params && params.row && params.row.isRequested
        const { PK, SK } = params?.row ?? {}
        return (
          <Link
            to={`/deals/${PK}/${SK}/review`}
            style={{
              textDecoration: 'none',
              color: 'inherit',
              backgroundColor: 'transparent',
            }}
          >
            <Tooltip
              title={<Typography sx={{ backgroundColor: 'transparent' }}>{params.value}</Typography>}
              enterDelay={2000}
              sx={{
                backgroundColor: 'transparent', // Transparent background for tooltip
                boxShadow: 'none', // Remove box shadow
                '.MuiTooltip-tooltip': {
                  backgroundColor: 'transparent', // Make tooltip content background transparent
                  boxShadow: 'none', // Remove box shadow around tooltip
                  padding: 0, // Remove padding if you want only the text to be visible
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                {isApproved ? (
                  <Typography
                    sx={{ fontSize: theme.palette.text.size, color: theme.palette.text.secondary, textAlign: 'left' }}
                  >
                    {params.value}
                  </Typography>
                ) : (
                  <Typography
                    sx={{ fontSize: theme.palette.text.size, color: theme.palette.text.primary, textAlign: 'left' }}
                  >
                    {params.value}
                  </Typography>
                )}
              </Box>
            </Tooltip>
          </Link>
        )
      },
    },
    // {
    //   field: 'contractId',
    //   headerName: 'Contract',
    //   width: 100,
    //   flex: 0.55,
    //   headerAlign: 'center',
    //   valueGetter: params => {
    //     return params
    //   },
    //   renderCell: params => {
    //     const previewUrl = `https://app.pandadoc.com/a/#/documents`
    //     const contractId = params && params.value ? params.value : null
    //     const contractUrl = previewUrl && contractId ? `${previewUrl}/${contractId}` : null
    //     return (
    //       <Box
    //         sx={{
    //           display: 'flex',
    //           flexDirection: 'row',
    //           justifyContent: 'center',
    //           alignItems: 'center',
    //           height: '100%',
    //           padding: densityStyles.padding,
    //           gap: densityStyles.gap,
    //         }}
    //       >
    //         {contractId ? (
    //           <PandaDocViewer contractId={contractId} height={32} width={32} />
    //         ) : (
    //           <DisabledIconButton disabled>
    //             <PandaDocLinkIcon size={densityStyles.globeSize} />
    //           </DisabledIconButton>
    //         )}
    //       </Box>
    //     )
    //   },
    // },
    {
      field: 'progressPercentage',
      headerName: 'Progress',
      width: 60,
      flex: 0.5,
      valueGetter: params => {
        return params
      },
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      renderCell: params => {
        // const { progressPercentage, progress } = params.row
        const progressPercentage = params.value

        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              width: '100%',
              height: '100%',
            }}
          >
            {/* <LinearProgress
              variant="determinate"
              value={progressPercentage}
              sx={{
                width: '100%',
                height: 25,
                borderRadius: 5,
                backgroundColor: 'lightgray',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: progressPercentage === 100 ? 'rgb(6, 255, 167, 0.6)' : 'rgba(45, 85, 255, 1)',
                },
              }}
            /> */}
            <ProgressCell
              params={{ ...params, row: { ...params?.row, isParent: true } }}
              densityStyles={densityStyles}
            />
            <Box
              sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            ></Box>
          </Box>
        )
      },
    },
    {
      field: 'requestedTime',
      headerName: 'Time Requested',
      width: 100,
      flex: 0.5,
      valueGetter: params => {
        return params
      },
      renderCell: params => {
        return (
          <Box>
            {params && params.value ? formatDayjsDate(params && params.value ? params.value : 'No Modified Time') : '—'}
          </Box>
        )
      },
    },
    {
      field: 'requestedBy',
      headerName: 'Requested By',
      width: 100,
      flex: 0.5,
      valueGetter: params => {
        const userId = params ? params : null

        const user = users?.find(u => u.id === userId)
        return userId ? userId : null
      },
      renderCell: params => {
        const userId = params && params.value ? params.value : null

        const user = users?.find(u => u.id === userId)
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              padding: densityStyles.padding,
              gap: densityStyles.gap,
            }}
          >
            {user ? (
              <EntityAvatar name={user.name} photoUrl={user.image_512} size={densityStyles.size} tooltip />
            ) : (
              <Box
                sx={{
                  width: densityStyles.avatarSize,
                  height: densityStyles.avatarSize,
                  borderRadius: '50%',
                  backgroundColor: 'lightgray',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  color: 'text.secondary',
                  fontSize: densityStyles.fontSize,
                }}
              >
                <IconButton disabled>
                  <Avatar />
                </IconButton>
              </Box>
            )}
          </Box>
        )
      },
    },
    {
      field: 'approvers',
      headerName: 'Approvers',
      width: 100,
      flex: 1,
      filterOperators: [customContainsApprover],
      valueGetter: params => {
        const approvers = params || []
        const approverIds = approvers.map(approver => approver.id)
        return { approvers, approverIds }
      },
      renderCell: params => <ApproverBadge approvers={params?.value?.approvers ?? []} densityStyles={densityStyles} />,
    },
  ]
}
